import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000, // 60 second timeout
  headers: {
    'Content-Type': 'application/json',
  },
});

export interface ChatResponse {
  response: string;
  session_id: string;
  metadata?: {
    provider?: string;
    sources?: string[];
    retrieved_docs?: number;
  };
}

export interface HealthResponse {
  status: string;
  message: string;
  components?: {
    embedding_service?: string;
    vector_db_service?: string;
    llm_service?: string;
  };
  sessions?: {
    active_sessions?: number;
    total_messages?: number;
  };
}

export interface FeedbackResponse {
  status: string;
  message: string;
}

export class ChatStreamError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'ChatStreamError';
    this.status = status;
  }
}

export interface ChatStreamMetadata {
  session_id: string;
  sources: string[];
  retrieved_docs: number;
}

export interface ChatStreamDone {
  provider: string;
  truncated?: boolean;
}

export interface ChatStreamCallbacks {
  onMetadata?: (data: ChatStreamMetadata) => void;
  onChunk: (text: string) => void;
  onDone: (data: ChatStreamDone) => void;
}

export interface StatusResponse {
  status: string;
  services_initialized: boolean;
}

export interface WakeUpResponse {
  status: string;
  message: string;
  tars_quote?: string;
  components?: {
    embedding_service?: string;
    vector_db_service?: string;
    llm_service?: string;
  };
  initialized_at?: string;
}

export const chatAPI = {
  sendMessage: async (message: string, sessionId: string): Promise<ChatResponse> => {
    const response = await api.post<ChatResponse>('/chat', {
      message,
      session_id: sessionId,
    });
    return response.data;
  },

  streamMessage: async (
    message: string,
    sessionId: string,
    callbacks: ChatStreamCallbacks,
    signal?: AbortSignal
  ): Promise<void> => {
    const response = await fetch(`${API_BASE_URL}/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, session_id: sessionId }),
      signal,
    });

    if (!response.ok || !response.body) {
      const body = await response.json().catch(() => null);
      throw new ChatStreamError(body?.error || 'Request failed', response.status);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    const processEvent = (rawEvent: string) => {
      let eventName = 'message';
      let data = '';
      for (const line of rawEvent.split('\n')) {
        if (line.startsWith('event:')) eventName = line.slice(6).trim();
        else if (line.startsWith('data:')) data = line.slice(5).trim();
      }
      if (!data) return;

      let parsed;
      try {
        parsed = JSON.parse(data);
      } catch (e) {
        console.error('Failed to parse SSE event data:', data, e);
        return;
      }

      if (eventName === 'metadata') callbacks.onMetadata?.(parsed);
      else if (eventName === 'chunk') callbacks.onChunk(parsed.text);
      else if (eventName === 'done') callbacks.onDone(parsed);
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const rawEvents = buffer.split('\n\n');
      buffer = rawEvents.pop() ?? '';

      for (const rawEvent of rawEvents) processEvent(rawEvent);
    }

    // Flush any trailing bytes the decoder held back, and process a final
    // event left in the buffer in case the stream ended without \n\n.
    buffer += decoder.decode();
    if (buffer.trim()) processEvent(buffer);
  },

  checkHealth: async (): Promise<HealthResponse> => {
    const response = await api.get<HealthResponse>('/health');
    return response.data;
  },

  checkStatus: async (): Promise<StatusResponse> => {
    const response = await api.get<StatusResponse>('/status');
    return response.data;
  },

  wakeUp: async (): Promise<WakeUpResponse> => {
    const response = await api.get<WakeUpResponse>('/wake-up');
    return response.data;
  },

  sendFeedback: async (sessionId: string, rating: number, comment?: string): Promise<FeedbackResponse> => {
    const response = await api.post<FeedbackResponse>('/feedback', {
      session_id: sessionId,
      rating,
      comment,
    });
    return response.data;
  },
};

export default api;
